import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
} from '@mui/material';
import { Warning } from '@mui/icons-material';

interface Service {
  id: string;
  name: string;
  description: string;
  category: string;
  isActive: boolean;
}

interface DeleteServiceDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  service: Service | null;
  loading?: boolean;
}

const DeleteServiceDialog: React.FC<DeleteServiceDialogProps> = ({
  open,
  onClose,
  onConfirm,
  service,
  loading = false,
}) => {
  if (!service) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Warning color="warning" />
          <Typography variant="h6">Delete Service</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" gutterBottom>
          Are you sure you want to delete the service "{service.name}"?
        </Typography>
        
        <Typography variant="body2" color="text.secondary" paragraph>
          Category: {service.category}
        </Typography>

        <Typography variant="body2" color="text.secondary" paragraph>
          {service.description}
        </Typography>

        <Alert severity="warning" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>Warning:</strong> This action cannot be undone. Deleting this service will:
          </Typography>
          <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
            <li>Remove the service from all future appointments</li>
            <li>Make it unavailable for new bookings</li>
            <li>Remove it from any existing packages</li>
            <li>Affect staff assignments and schedules</li>
          </ul>
          <Typography variant="body2">
            Consider deactivating the service instead if you want to keep historical data.
          </Typography>
        </Alert>

        {service.isActive && (
          <Alert severity="info" sx={{ mt: 1 }}>
            <Typography variant="body2">
              This service is currently <strong>active</strong>. You may want to deactivate it first 
              to prevent new bookings before deletion.
            </Typography>
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button 
          variant="contained" 
          color="error" 
          onClick={onConfirm}
          disabled={loading}
        >
          {loading ? 'Deleting...' : 'Delete Service'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteServiceDialog;